import { emailRegEx } from '@logto/core-kit';
import { SignInIdentifier } from '@logto/schemas';

export type LoginIdentifierSelection = 'auto' | SignInIdentifier;
export type LoginIdentifierError =
  'identifier_invalid' | 'identifier_ambiguous' | 'country_required' | 'country_not_enabled';
export type LoginIdentifierResult =
  | {
      ok: true;
      type: SignInIdentifier;
      /** User-facing value. Never display the native username alias. */
      canonical: string;
      /** Value for the fixed native Logto password/code API, converted exactly once. */
      provider: string;
    }
  | { ok: false; error: LoginIdentifierError };

const handlePattern = /^[A-Za-z0-9_]{5,15}$/;
const domesticPhonePattern = /^1[3-9][0-9]{9}$/;
const invalid = (): LoginIdentifierResult => ({ ok: false, error: 'identifier_invalid' });

/** Pure format classification, not an account lookup. Passwords/OTPs never enter this function. */
export function resolveLoginIdentifier(
  input: string,
  selection: LoginIdentifierSelection = 'auto',
  countryCallingCode?: string
): LoginIdentifierResult {
  const value = input.trim();
  if (!value) {
    return invalid();
  }

  if (selection === 'auto') {
    if (value.startsWith('@')) {
      return resolveLoginIdentifier(value, SignInIdentifier.Username);
    }
    if (value.startsWith('+')) {
      return resolveLoginIdentifier(value, SignInIdentifier.Phone);
    }
    if (value.includes('@')) {
      return resolveLoginIdentifier(value, SignInIdentifier.Email);
    }
    if (handlePattern.test(value)) {
      if (domesticPhonePattern.test(value)) {
        return { ok: false, error: 'identifier_ambiguous' };
      }
      return resolveLoginIdentifier(value, SignInIdentifier.Username);
    }
    if (domesticPhonePattern.test(value.replace(/[ -]/g, ''))) {
      return resolveLoginIdentifier(value, SignInIdentifier.Phone, countryCallingCode);
    }
    return invalid();
  }

  if (selection === SignInIdentifier.Username) {
    const handle = value.startsWith('@') ? value.slice(1) : value;
    // Validate ASCII before lowering: do not turn a Unicode look-alike into an existing Handle.
    if (!handlePattern.test(handle)) {
      return invalid();
    }
    const canonical = handle.toLowerCase();
    return { ok: true, type: selection, canonical, provider: `vh_${canonical}` };
  }

  if (selection === SignInIdentifier.Email) {
    // Native Logto owns email normalization; preserve dots, case and +tags at this boundary.
    return emailRegEx.test(value)
      ? { ok: true, type: selection, canonical: value, provider: value }
      : invalid();
  }

  const compact = value.replace(/[ -]/g, '');
  if (compact.startsWith('+')) {
    if (!compact.startsWith('+86')) {
      return { ok: false, error: 'country_not_enabled' };
    }
    if (!domesticPhonePattern.test(compact.slice(3))) {
      return invalid();
    }
    return {
      ok: true,
      type: SignInIdentifier.Phone,
      canonical: compact,
      provider: compact.slice(1),
    };
  }
  if (!countryCallingCode) {
    return { ok: false, error: 'country_required' };
  }
  if (countryCallingCode !== '+86') {
    return { ok: false, error: 'country_not_enabled' };
  }
  if (!domesticPhonePattern.test(compact)) {
    return invalid();
  }
  return {
    ok: true,
    type: SignInIdentifier.Phone,
    canonical: `+86${compact}`,
    provider: `86${compact}`,
  };
}
